/* eslint-disable react/jsx-no-bind */
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';

import { BASE_URL } from '../../constants/baseUrl';
import { tokens } from '../../theme';
import axiosInstance from '../../utils/axiosInstance';

const AppUpdate = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateComplete, setUpdateComplete] = useState('');
  const [formData, setFormData] = useState({
    os_type: 0,
    build_number: 0,
    version: '',
    release_date: '',
    force_update: true,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setUpdateComplete('');

    try {
      await axiosInstance.post(
        'https://devapi.swiftcarry.in/api/v1/app/version',
        formData,
      );

      setUpdateComplete(
        `${formData.os_type === 0 ? 'Android' : 'iOS'} app updated successfully!`,
      );
    } catch (error) {
      setUpdateComplete('Failed to update app. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box m="20px">
      <Card
        sx={{
          maxWidth: 600,
          mx: 'auto',
          mt: 4,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
          },
        }}
      >
        <CardContent
          sx={{
            p: 4,
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
            },
          }}
        >
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.swiftcarry[100]} 0%, #4facfe 100%)`,
                boxShadow: '0 10px 30px rgba(0, 201, 150, 0.3)',
                mb: 2,
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
                animation: 'pulse 2s infinite',
              }}
            >
              <SystemUpdateAltIcon
                sx={{
                  fontSize: 40,
                  color: 'white',
                }}
              />
            </Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              mb={2}
              sx={{
                background: `linear-gradient(135deg, ${colors.swiftcarry[100]} 0%, #667eea 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              App Version Update
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              '& .MuiTextField-root, & .MuiFormControl-root': {
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(255, 255, 255, 1)',
                    boxShadow: `0 0 0 2px ${colors.swiftcarry[100]}40`,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  fontWeight: 500,
                },
                '& .MuiOutlinedInput-input': {
                  padding: '16px 14px',
                  fontSize: '16px',
                },
              },
            }}
          >
            <TextField
              fullWidth
              label="Build Number"
              type="number"
              value={formData.build_number}
              onChange={(e) =>
                handleInputChange('build_number', parseInt(e.target.value))
              }
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Version"
              value={formData.version}
              onChange={(e) => handleInputChange('version', e.target.value)}
              margin="normal"
              required
              placeholder="e.g., 1.0.0"
            />

            <TextField
              fullWidth
              label="Minimum Version (Who can update)"
              value={formData.minimum_version}
              onChange={(e) =>
                handleInputChange('minimum_version', e.target.value)
              }
              margin="normal"
              required
              placeholder="e.g., 0.9.0"
            />

            <TextField
              fullWidth
              label="Release Date"
              type="date"
              value={formData.release_date}
              onChange={(e) =>
                handleInputChange('release_date', e.target.value)
              }
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Platform</InputLabel>
              <Select
                value={formData.os_type}
                onChange={(e) => handleInputChange('os_type', e.target.value)}
                label="Platform"
              >
                <MenuItem value={0}>🤖 Android</MenuItem>
                <MenuItem value={1}>🍎 iOS</MenuItem>
              </Select>
            </FormControl>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 3,
                mb: 3,
                p: 2,
                borderRadius: '12px',
                background:
                  'linear-gradient(135deg, rgba(0, 201, 150, 0.1) 0%, rgba(102, 126, 234, 0.1) 100%)',
                border: '1px solid rgba(0, 201, 150, 0.2)',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.force_update}
                    onChange={(e) =>
                      handleInputChange('force_update', e.target.checked)
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: colors.swiftcarry[100],
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: colors.swiftcarry[100],
                        },
                    }}
                  />
                }
                label="Force Update"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 600,
                    color: colors.swiftcarry[100],
                  },
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isUpdating}
              startIcon={
                isUpdating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                background: `linear-gradient(135deg, ${colors.swiftcarry[100]} 0%, #4facfe 100%)`,
                color: 'white',
                py: 2,
                borderRadius: '16px',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 8px 32px ${colors.swiftcarry[100]}40`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 16px 48px ${colors.swiftcarry[100]}50`,
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                  transform: 'none',
                  boxShadow: 'none',
                },
              }}
            >
              {isUpdating ? 'Updating...' : '🚀 Update App'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {updateComplete && (
        <Alert
          severity={updateComplete.includes('Failed') ? 'error' : 'success'}
          sx={{
            mt: 3,
            maxWidth: 600,
            mx: 'auto',
            borderRadius: '16px',
            backdropFilter: 'blur(20px)',
            background: updateComplete.includes('Failed')
              ? 'rgba(244, 67, 54, 0.1)'
              : 'rgba(76, 175, 80, 0.1)',
            border: updateComplete.includes('Failed')
              ? '1px solid rgba(244, 67, 54, 0.3)'
              : '1px solid rgba(76, 175, 80, 0.3)',
            animation: 'slideInUp 0.5s ease-out',
            '@keyframes slideInUp': {
              from: {
                transform: 'translateY(30px)',
                opacity: 0,
              },
              to: {
                transform: 'translateY(0)',
                opacity: 1,
              },
            },
          }}
        >
          {updateComplete}
        </Alert>
      )}
    </Box>
  );
};

export default AppUpdate;
